import React from "react";
import { Text, Image } from "@react-pdf/renderer";
import "./assets/fonts";
import {
  ELEMENT_LAYOUT,
  GRADE_DISPLAY_BOX,
  MONTSERRAT_ASCENT,
  PAGE,
  SIMPLE_LAYOUT,
  SIMPLE_LINE_WIDTHS,
} from "./layout/constants";
import {
  autographLineText,
  gradeDisplayCenterX,
  gradeLabelFontSize,
  gradeLineText,
} from "./gradeHelpers";
import { getVisualStyle } from "./labelVisual";
import { pickLayout } from "./pickLayout";

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

/**
 * ReportLab usa origem no canto inferior esquerdo (y sobe).
 * @react-pdf usa origem no topo (y desce). Converte y ReportLab → top.
 */
const rlTop = (yFromBottom, height = 0) => PAGE.height - yFromBottom - height;

/** Caixa (imagem): y ReportLab = canto inferior esquerdo. */
const absBox = (layout, slot) => {
  const height = layout.height || 0;
  const y = layout.y + (slot.y_offset || 0);
  return {
    left: layout.x + (slot.x_offset || 0),
    top: rlTop(y, height),
    width: layout.width,
    height,
  };
};

/**
 * Texto: y ReportLab = baseline; o @react-pdf posiciona pelo topo da caixa e
 * desce o ascender até a baseline. Descontar o ascender deixa as duas baselines
 * no mesmo lugar.
 */
const absText = (layout, slot, fontSize) => {
  const y = layout.y + (slot.y_offset || 0);
  return {
    left: layout.x + (slot.x_offset || 0),
    top: rlTop(y, MONTSERRAT_ASCENT * fontSize),
  };
};

const LabelText = ({ layout, slot, children, textColor, centered = false, centerX, fontOverride }) => {
  if (isBlank(children)) return null;
  const [fontFamily, fontSize] = fontOverride || layout.font;
  const pos = absText(layout, slot, fontSize);
  const style = {
    position: "absolute",
    fontFamily,
    fontSize,
    color: textColor,
  };

  if (centered && centerX !== undefined) {
    const boxWidth = GRADE_DISPLAY_BOX.width;
    return (
      <Text
        style={{
          ...style,
          left: centerX - boxWidth / 2,
          top: pos.top,
          width: boxWidth,
          textAlign: "center",
        }}
      >
        {String(children)}
      </Text>
    );
  }

  return (
    <Text style={{ ...style, left: pos.left, top: pos.top }}>
      {String(children)}
    </Text>
  );
};

const SimpleLabelText = ({ layoutKey, slot, children, textColor, rightAligned = false }) => {
  if (isBlank(children)) return null;
  const layout = SIMPLE_LAYOUT[layoutKey];
  const [fontFamily, fontSize] = layout.font;
  const pos = absText(layout, slot, fontSize);
  // Linha inteira: nunca quebra (o validador já barra o que não cabe na linha).
  const width = SIMPLE_LINE_WIDTHS[layoutKey];
  const rightX = layout.x + (slot.x_offset || 0);

  if (rightAligned) {
    return (
      <Text
        style={{
          position: "absolute",
          left: rightX - width,
          top: pos.top,
          width,
          textAlign: "right",
          fontFamily,
          fontSize,
          color: textColor,
        }}
      >
        {String(children)}
      </Text>
    );
  }

  return (
    <Text
      style={{
        position: "absolute",
        left: pos.left,
        top: pos.top,
        fontFamily,
        fontSize,
        color: textColor,
      }}
    >
      {String(children)}
    </Text>
  );
};

const WithSubgradesLabel = ({ card, slot, textColor, qrDataUrl }) => {
  const gradeCenterX = gradeDisplayCenterX(slot);
  const infosText = `| ${card.ano || ""} | ${card.idioma || ""} | ${card.num || ""}`;
  const qrLayout = ELEMENT_LAYOUT.qr;
  const qrPos = absBox(qrLayout, slot);

  return (
    <>
      <LabelText layout={ELEMENT_LAYOUT.nome} slot={slot} textColor={textColor}>
        {card.nome}
      </LabelText>
      <LabelText layout={ELEMENT_LAYOUT.edition} slot={slot} textColor={textColor}>
        {card.edition}
      </LabelText>
      <LabelText layout={ELEMENT_LAYOUT.raridade} slot={slot} textColor={textColor}>
        {card.raridade}
      </LabelText>
      <LabelText layout={ELEMENT_LAYOUT.infos} slot={slot} textColor={textColor}>
        {infosText}
      </LabelText>
      <LabelText layout={ELEMENT_LAYOUT.surface} slot={slot} textColor={textColor}>
        {`Surface - ${card.notaSurface}`}
      </LabelText>
      <LabelText layout={ELEMENT_LAYOUT.centering} slot={slot} textColor={textColor}>
        {`Centering - ${card.notaCenter}`}
      </LabelText>
      <LabelText layout={ELEMENT_LAYOUT.edges} slot={slot} textColor={textColor}>
        {`Edges - ${card.notaEdge}`}
      </LabelText>
      <LabelText layout={ELEMENT_LAYOUT.corners} slot={slot} textColor={textColor}>
        {`Corners - ${card.notaCorner}`}
      </LabelText>
      <LabelText
        layout={ELEMENT_LAYOUT.nota_geral}
        slot={slot}
        textColor={textColor}
        centered
        centerX={gradeCenterX}
      >
        {card.notaGeral}
      </LabelText>
      <LabelText
        layout={ELEMENT_LAYOUT.nome_nota}
        slot={slot}
        textColor={textColor}
        centered
        centerX={gradeCenterX}
        fontOverride={["Montserrat-Light", gradeLabelFontSize(card.notaGeral)]}
      >
        {card.nome_nota}
      </LabelText>
      <LabelText
        layout={ELEMENT_LAYOUT.serial}
        slot={slot}
        textColor={textColor}
        centered
        centerX={gradeCenterX}
      >
        {card.serial}
      </LabelText>
      {qrDataUrl ? (
        <Image
          src={qrDataUrl}
          style={{
            position: "absolute",
            left: qrPos.left,
            top: qrPos.top,
            width: qrLayout.width,
            height: qrLayout.height,
            // Lambda: ImageReader do QR rotacionado 180° (verso da dobra).
            transform: "rotate(180deg)",
          }}
        />
      ) : null}
    </>
  );
};

const SimpleLabel = ({ card, slot, textColor, qrDataUrl, scenario }) => {
  const qrLayout = SIMPLE_LAYOUT.qr;
  const qrPos = absBox(qrLayout, slot);
  const showAuthentic = scenario === "authentic" || scenario === "authentic_auto";
  const showGrade = scenario === "no_subgrades" || scenario === "no_subgrades_auto";
  const showAuto = scenario === "authentic_auto" || scenario === "no_subgrades_auto";

  return (
    <>
      <SimpleLabelText layoutKey="name" slot={slot} textColor={textColor}>
        {card.nome}
      </SimpleLabelText>
      <SimpleLabelText layoutKey="edition" slot={slot} textColor={textColor}>
        {card.edition}
      </SimpleLabelText>
      {showAuthentic ? (
        <SimpleLabelText layoutKey="right_top" slot={slot} textColor={textColor} rightAligned>
          AUTHENTIC
        </SimpleLabelText>
      ) : null}
      <SimpleLabelText layoutKey="number" slot={slot} textColor={textColor} rightAligned>
        {card.num}
      </SimpleLabelText>
      <SimpleLabelText layoutKey="raridade" slot={slot} textColor={textColor}>
        {card.raridade}
      </SimpleLabelText>
      {showGrade ? (
        <SimpleLabelText layoutKey="right_middle" slot={slot} textColor={textColor} rightAligned>
          {gradeLineText(card)}
        </SimpleLabelText>
      ) : null}
      <SimpleLabelText layoutKey="ano" slot={slot} textColor={textColor}>
        {card.ano}
      </SimpleLabelText>
      {showAuto ? (
        <SimpleLabelText layoutKey="right_auto" slot={slot} textColor={textColor} rightAligned>
          {autographLineText(card.notaAutografo)}
        </SimpleLabelText>
      ) : null}
      <SimpleLabelText layoutKey="idioma" slot={slot} textColor={textColor}>
        {card.idioma}
      </SimpleLabelText>
      <SimpleLabelText layoutKey="serial" slot={slot} textColor={textColor} rightAligned>
        {card.serial}
      </SimpleLabelText>
      {qrDataUrl ? (
        <Image
          src={qrDataUrl}
          style={{
            position: "absolute",
            left: qrPos.left,
            top: qrPos.top,
            width: qrLayout.width,
            height: qrLayout.height,
            // Lambda: ImageReader do QR rotacionado 180° (verso da dobra).
            transform: "rotate(180deg)",
          }}
        />
      ) : null}
    </>
  );
};

/**
 * Só texto e QR: moldura, fundo e linhas de corte vêm do template PDF vetorial
 * carimbado por baixo em mergeWithTemplate (igual ao merge_page da Lambda).
 */
const LabelTagPdf = ({ card, slot }) => {
  if (!card || !card.scenario) return null;

  const visual = card?.visual || "majorca_legado";
  const layout = pickLayout(card.scenario);
  const { textColor } = getVisualStyle(visual);

  return (
    <>
      {layout === "with_subgrades" ? (
        <WithSubgradesLabel
          card={card}
          slot={slot}
          textColor={textColor}
          qrDataUrl={card.qrDataUrl}
        />
      ) : (
        <SimpleLabel
          card={card}
          slot={slot}
          textColor={textColor}
          qrDataUrl={card.qrDataUrl}
          scenario={card.scenario}
        />
      )}
    </>
  );
};

export default LabelTagPdf;
